import type { CustomFieldDefinition } from "@repo/app-types/entities/customField";
import {
  CustomFieldResourceType,
  customFieldResourceTypeLabels,
  removeDataExtractionFieldNamePrefix,
} from "@repo/app-types/entities/customField";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size, toHumanReadableString } from "@repo/ui/helpers/helpers";
import { CenteredModal } from "@repo/ui/Modal/centeredModal";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import type { SimpleSelectOption } from "@repo/ui/Select/simpleSelect";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { TableSkeleton } from "@repo/ui/Skeletons/skeletons";
import { SmallTimeStamp } from "@repo/ui/SmallTimeStamp/smallTimeStamp";
import {
  IconFilter,
  IconPencil,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { type FC, useMemo, useState } from "react";
import { Dialog, DialogTrigger } from "react-aria-components";
import {
  useAllDataExtractionFieldDefinitionsQuery,
  useDeleteDataExtractionFieldDefinitionMutation,
} from "../../../api/entities/dataExtractionField.js";
import { PageBody, PageLayout } from "../../../components/layout/pageLayout.js";
import { useIsFeatureFlagEnabled } from "../../../helpers/featureFlags/hooks.js";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";
import { settingsSections } from "../settingsSections.js";
import { SettingsTable } from "../shared/settingsTable.js";
import { SettingsTableBodySection } from "../shared/settingsTableBodySection.js";
import { SettingsTableDataCell } from "../shared/settingsTableDataCell.js";
import { SettingsTableEmptyBodyContent } from "../shared/settingsTableEmptyBodyContent.js";
import { SettingsTableHeaderCell } from "../shared/settingsTableHeaderCell.js";
import { SettingsTableHeaderSection } from "../shared/settingsTableHeaderSection.js";
import { CreateDataExtractionFieldDefinitionForm } from "./createDataExtractionFieldDefinitionForm.js";
import { EditDataExtractionFieldDefinitionModal } from "./editDataExtractionFieldDefinitionModal.js";

export type DataExtractionResourceType =
  | typeof CustomFieldResourceType.INTERVIEW
  | typeof CustomFieldResourceType.GRADING;

type ResourceTypeFilterValue = null | DataExtractionResourceType;

export const DataExtractionSettingsSection: FC = () => {
  const hasRecruiting = !useIsFeatureFlagEnabled(FeatureFlagKey.HideHireTab);
  const hasTeamPerformanceTab = useIsFeatureFlagEnabled(
    FeatureFlagKey.TeamPerformanceTab,
  );

  const { data: dataExtractionFields, isLoading } =
    useAllDataExtractionFieldDefinitionsQuery();

  // Determine which resource types have existing fields
  const hasExistingInterviewFields = useMemo(
    () =>
      dataExtractionFields?.some(
        (field) => field.resource_type === CustomFieldResourceType.INTERVIEW,
      ) ?? false,
    [dataExtractionFields],
  );

  const hasExistingGradingFields = useMemo(
    () =>
      dataExtractionFields?.some(
        (field) => field.resource_type === CustomFieldResourceType.GRADING,
      ) ?? false,
    [dataExtractionFields],
  );

  // Determine which resource types should be available
  const isInterviewOptionVisible = hasRecruiting || hasExistingInterviewFields;
  const isGradingOptionVisible =
    hasTeamPerformanceTab || hasExistingGradingFields;

  // Build filter options based on available resource types
  const resourceTypeFilterOptions = useMemo(() => {
    const options: SimpleSelectOption<ResourceTypeFilterValue>[] = [
      { label: "All types", value: null },
    ];

    if (isInterviewOptionVisible) {
      options.push({
        label: customFieldResourceTypeLabels[CustomFieldResourceType.INTERVIEW],
        value: CustomFieldResourceType.INTERVIEW,
      });
    }

    if (isGradingOptionVisible) {
      options.push({
        label: customFieldResourceTypeLabels[CustomFieldResourceType.GRADING],
        value: CustomFieldResourceType.GRADING,
      });
    }

    return options;
  }, [isInterviewOptionVisible, isGradingOptionVisible]);

  // Build available resource types for create form
  const availableResourceTypes = useMemo(() => {
    const types: DataExtractionResourceType[] = [];
    if (isInterviewOptionVisible) {
      types.push(CustomFieldResourceType.INTERVIEW);
    }
    if (isGradingOptionVisible) {
      types.push(CustomFieldResourceType.GRADING);
    }
    return types;
  }, [isInterviewOptionVisible, isGradingOptionVisible]);

  // Build description based on feature flags
  const sectionDescription = useMemo(() => {
    const destinations: string[] = [];
    if (hasRecruiting) {
      destinations.push("job types");
    }
    if (hasTeamPerformanceTab) {
      destinations.push("playbooks");
    }
    const destinationText =
      destinations.length > 0
        ? destinations.join(" or ")
        : "job types or playbooks";
    return `Define fields to extract structured data. Once created, these fields can be added to ${destinationText}.`;
  }, [hasRecruiting, hasTeamPerformanceTab]);

  const [selectedResourceType, setSelectedResourceType] =
    useState<ResourceTypeFilterValue>(null);

  const [deleteDataExtractionField, { isLoading: isDeleting }] =
    useDeleteDataExtractionFieldDefinitionMutation();

  const [fieldIdToDelete, setFieldIdToDelete] = useState<string | undefined>(
    undefined,
  );

  const [fieldToEdit, setFieldToEdit] = useState<
    CustomFieldDefinition | undefined
  >(undefined);

  // Filter fields by resource type if needed
  const filteredDataExtractionFields = useMemo(() => {
    if (!dataExtractionFields) return [];
    if (selectedResourceType === null) return dataExtractionFields;

    return dataExtractionFields.filter(
      (field) => field.resource_type === selectedResourceType,
    );
  }, [dataExtractionFields, selectedResourceType]);

  // Determine if we should show multiple resource types (affects UI)
  const areMultipleResourceTypesVisible =
    isInterviewOptionVisible && isGradingOptionVisible;

  return (
    <PageLayout>
      <PageHeader
        width={PageWidth.Standard}
        title={settingsSections.dataExtraction.label}
        actions={
          <Can
            I={PermissionActions.CREATE}
            a={PermissionSubjects.DATA_EXTRACTION}
            passThrough={true}
          >
            {(allowed) => (
              <DialogTrigger>
                <Button
                  size={Size.SMALL}
                  variant={ButtonVariant.Primary}
                  icon={<IconPlus className="size-4" />}
                  isDisabled={!allowed}
                >
                  Create field
                </Button>

                <CenteredModal isDismissable size={Size.LARGE}>
                  <Dialog className="px-4 pt-5 pb-4 text-left sm:p-6">
                    {({ close }) => (
                      <>
                        <ModalHeader onClose={close}>
                          Create data extraction field
                        </ModalHeader>
                        <CreateDataExtractionFieldDefinitionForm
                          close={close}
                          availableResourceTypes={availableResourceTypes}
                        />
                      </>
                    )}
                  </Dialog>
                </CenteredModal>
              </DialogTrigger>
            )}
          </Can>
        }
      />
      <SimpleModal
        isOpen={!!fieldIdToDelete}
        onCancel={() => {
          setFieldIdToDelete(undefined);
        }}
        variant={SimpleModalVariant.Warning}
        title="Delete data extraction field"
        subtitle={
          "Are you sure you want to delete this field? This action can't be undone."
        }
        confirmButtonText="Delete field"
        onConfirm={() => {
          if (fieldIdToDelete) {
            deleteDataExtractionField(fieldIdToDelete);
          }

          setFieldIdToDelete(undefined);
        }}
      />
      <EditDataExtractionFieldDefinitionModal
        field={fieldToEdit}
        onClose={() => setFieldToEdit(undefined)}
      />
      <PageBody width={PageWidth.Standard}>
        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">
            {sectionDescription}
          </div>

          {areMultipleResourceTypesVisible ? (
            <div className="flex w-64 items-center space-x-2">
              <SimpleSelect<ResourceTypeFilterValue>
                size={Size.SMALL}
                leftAccessory={<IconFilter className="h-4 w-4" />}
                options={resourceTypeFilterOptions}
                value={selectedResourceType}
                onChange={setSelectedResourceType}
                placeholder="Filter by type"
              />
            </div>
          ) : null}

          <SettingsTable>
            <SettingsTableHeaderSection>
              <tr>
                {areMultipleResourceTypesVisible ? (
                  <SettingsTableHeaderCell className="w-[15%]">
                    Resource
                  </SettingsTableHeaderCell>
                ) : null}
                <SettingsTableHeaderCell
                  className={
                    areMultipleResourceTypesVisible ? "w-[25%]" : "w-[30%]"
                  }
                >
                  Field name
                </SettingsTableHeaderCell>
                <SettingsTableHeaderCell
                  className={
                    areMultipleResourceTypesVisible ? "w-[25%]" : "w-[30%]"
                  }
                >
                  Field key
                </SettingsTableHeaderCell>
                <SettingsTableHeaderCell
                  className={
                    areMultipleResourceTypesVisible ? "w-[15%]" : "w-[20%]"
                  }
                >
                  Type
                </SettingsTableHeaderCell>
                <SettingsTableHeaderCell className="w-[20%]">
                  Created
                </SettingsTableHeaderCell>
              </tr>
            </SettingsTableHeaderSection>
            <SettingsTableBodySection>
              {isLoading ? <TableSkeleton /> : null}

              {filteredDataExtractionFields.length === 0 ? (
                <SettingsTableEmptyBodyContent
                  colSpan={areMultipleResourceTypesVisible ? 5 : 4}
                >
                  {dataExtractionFields?.length === 0
                    ? "No data extraction fields have been created yet."
                    : "No data extraction fields match the selected filter."}
                </SettingsTableEmptyBodyContent>
              ) : null}

              {filteredDataExtractionFields.map((field) => (
                <tr key={field.id} className="group relative">
                  {areMultipleResourceTypesVisible ? (
                    <SettingsTableDataCell>
                      <span className="text-sm text-muted-foreground">
                        {customFieldResourceTypeLabels[field.resource_type]}
                      </span>
                    </SettingsTableDataCell>
                  ) : null}
                  <SettingsTableDataCell>
                    <p className="font-semibold break-words whitespace-normal">
                      {field.display_name}
                    </p>
                  </SettingsTableDataCell>
                  <SettingsTableDataCell>
                    <span className="font-mono text-xs text-muted-foreground">
                      {removeDataExtractionFieldNamePrefix(field)}
                    </span>
                  </SettingsTableDataCell>
                  <SettingsTableDataCell>
                    <p className="text-sm text-muted-foreground">
                      {toHumanReadableString(field.field_type)}
                    </p>
                  </SettingsTableDataCell>
                  <SettingsTableDataCell>
                    <SmallTimeStamp
                      timestamp={field.created_at}
                      className="text-sm text-muted-foreground"
                    />

                    <div className="absolute top-1/2 right-3 hidden -translate-y-1/2 cursor-pointer items-center space-x-2 text-muted-foreground group-hover:flex">
                      <Can
                        I={PermissionActions.UPDATE}
                        a={PermissionSubjects.DATA_EXTRACTION}
                      >
                        <IconButton
                          variant={ButtonVariant.Secondary}
                          icon={<IconPencil className="h-4 w-4" />}
                          onClick={() => {
                            setFieldToEdit(field);
                          }}
                        />
                      </Can>
                      <Can
                        I={PermissionActions.DELETE}
                        a={PermissionSubjects.DATA_EXTRACTION}
                      >
                        <IconButton
                          variant={ButtonVariant.Secondary}
                          icon={<IconTrash className="h-4 w-4" />}
                          onClick={() => {
                            setFieldIdToDelete(field.id);
                          }}
                        />
                      </Can>
                    </div>
                  </SettingsTableDataCell>
                </tr>
              ))}
            </SettingsTableBodySection>
          </SettingsTable>
        </div>
      </PageBody>
    </PageLayout>
  );
};
